import { RouteRecordRaw } from 'vue-router';
import { requireAuth, setDayjsTimezone } from '@/common/router/navigationGuard';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';
import { VIEW_MODE } from '@/common/define/common.define';
import { extractAccessibleRoutes } from '@/common/auth/auth.utils';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { MaxgaugeDatabaseSubMenu } from '@/common/utils';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import {
  requireDomainTargetOrTutorial,
  requireInstances,
  validateDatabaseOverviewPath,
} from '@/common/router/authGuard';

const PREFIX_PG = 'postgresql_';

const pgMenu: RouteRecordRaw[] = [
  {
    path: '/postgresql/',
    name: `${PREFIX_PG}Real Time Monitoring`,
    component: BaseViewport,
    children: [
      {
        path: 'multiView',
        name: `${PREFIX_PG}Multi View`,
        component: () => import('@/postgresql/views/multiView/MultiView.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'singleView',
        name: `${PREFIX_PG}Single View`,
        component: () => import('@/postgresql/views/singleView/SingleView.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [
      requireAuth,
      () => requireDomainTargetOrTutorial('database'),
      requireInstances,
      setDayjsTimezone,
      validateDatabaseOverviewPath,
    ],
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD,
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.REALTIME,
    },
  },
  {
    path: '/postgresql/',
    name: `${PREFIX_PG}SQL Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'topNAnalysis',
        name: `${PREFIX_PG}Top N Analysis`,
        component: () => import('@/database/views/topNAnalysis/postgresql/TopNAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'searchSql',
        name: `${PREFIX_PG}Search SQL`,
        component: () => import('@/postgresql/views/searchSql/SearchSqlView.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database')],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_POSTGRESQL_PA,
        [VIEW_MODE.MAXGAUGE]:
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_POSTGRESQL_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/postgresql/',
    name: `${PREFIX_PG}Session Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'searchSession',
        name: `${PREFIX_PG}Search Session`,
        component: () => import('@/database/views/searchSession/postgresql/SearchSession.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database'), requireInstances],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_POSTGRESQL_PA,
        [VIEW_MODE.MAXGAUGE]:
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_POSTGRESQL_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/postgresql/',
    name: `${PREFIX_PG}Performance Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'trendAnalysis',
        name: `${PREFIX_PG}Trend Analysis`,
        component: () => import('@/database/views/trendAnalysis/postgresql/TrendAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'parameterHistory',
        name: `${PREFIX_PG}Parameter History`,
        component: () =>
          import('@/database/views/parameterHistory/postgresql/ParameterHistory.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database')],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_POSTGRESQL_PA,
        [VIEW_MODE.MAXGAUGE]:
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_POSTGRESQL_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/postgresql/',
    name: `${PREFIX_PG}DataSpace Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'objectSize',
        name: `${PREFIX_PG}Object Size`,
        component: () => import('@/postgresql/views/objectSize/ObjectSize.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database'), requireInstances],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_POSTGRESQL_PA,
        [VIEW_MODE.MAXGAUGE]:
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_POSTGRESQL_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
];

const subMenuMappingPg = {
  instanceView: `${PREFIX_PG}Real Time Monitoring`,
  sqlAnalysis: `${PREFIX_PG}SQL Analysis`,
  sessionAnalysis: `${PREFIX_PG}Session Analysis`,
  performanceAnalysis: `${PREFIX_PG}Performance Analysis`,
  dataSpaceAnalysis: `${PREFIX_PG}DataSpace Analysis`,
};

const getPgNaviMenu = (subMenu?: MaxgaugeDatabaseSubMenu['postgresql']) => {
  const filteredPgMenu = extractAccessibleRoutes(pgMenu);

  if (subMenu && subMenuMappingPg[subMenu]) {
    return filteredPgMenu.filter((menu) => menu.name === subMenuMappingPg[subMenu]);
  }

  return filteredPgMenu.filter((menu) => menu.name !== subMenuMappingPg.instanceView);
};

export { pgMenu, getPgNaviMenu, PREFIX_PG };
